import { moment } from "@msuite/utils/date";
import { z } from "@msuite/utils/lib/zod";
import { defineMutator } from "@rocicorp/zero";
import { Guards } from "../handlers/guards.handlers";
import { Vehicles } from "../handlers/vehicles.handlers";
import { zql } from "../zql";

export const vehicleAssignmentMutators = {
	insert: defineMutator(
		z.object({
			// Ids must come from the call site: the mutator runs once on the client and
			// once on the server, and a generateId() in here yields two different rows —
			// the optimistic card is then torn down and remounted when the rebase lands.
			id: z.uuid(),
			crewId: z.uuid(),
			vehicleId: z.uuid(),
		}),
		async ({ tx, args, ctx }) => {
			const crew = await Guards.assertCrewOnEditableDay(tx, ctx, args.crewId);
			// The column is on the caller's board, which says nothing about the truck going into
			// it: the id comes from the client, and an assignment row carries no other tie to the
			// vehicle's organisation. Before the two cleanups, so a refused write reads nothing
			// out of the day it was never allowed to touch.
			await Guards.assertVehicleInOrg(tx, ctx, args.vehicleId);
			await Vehicles.cleanVehicleAssignments(tx, crew.planId, args.vehicleId);
			await Vehicles.cleanCurrentCrewVehicleAssignment(tx, args.crewId);
			return await tx.mutate.vehicleAssignments.insert({
				crewId: args.crewId,
				vehicleId: args.vehicleId,
				createdAt: moment().unix() as any,
				createdBy: ctx.userId,
				id: args.id,
				planId: crew.planId,
				// The plan's organisation, which assertCrewOnEditableDay has just established is the
				// caller's, named rather than left to the column default — the guard decides the
				// tenancy, not the connection.
				orgId: ctx.orgId,
			});
		},
	),
	delete: defineMutator(
		z.object({
			vehicleAssignmentId: z.uuid(),
		}),
		async ({ tx, args, ctx }) => {
			const assignment = await tx.run(
				zql.vehicleAssignments.where("id", "=", args.vehicleAssignmentId).one(),
			);
			if (!assignment) throw new Error("Vehicle assignment not found");
			await Guards.assertPlanOnEditableDay(tx, ctx, assignment.planId);
			return await tx.mutate.vehicleAssignments.delete({
				id: args.vehicleAssignmentId,
			});
		},
	),
};
